import * as React from 'react';
import { ChangeEvent, DragEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Uploader } from '../../components/Uploader';
import { uploadTemporaryFile } from '../../actions/form/uploadTemporaryFile';
import { resetFileState, setErrors } from '../../actions/form';
import { TThunkDispatch } from '../../types/redux';
import { selectForm } from '../../selectors/form';
import { validateMimeType } from '../Uploader/utils/validateMimeType';
import { validateSize } from '../Uploader/utils/validateSize';

const FILE_SIZE_ERROR = { cvFile: 'Размер файла не должен превышать 10МБ' };
const MAX_FILES = 3;

export const MultiUploaderContainer: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [dragDisabled, setDragDisabled] = useState(false);
  const dispatch = useDispatch<TThunkDispatch>();
  const {
    errors: { cvFile: error },
    isFileUploading,
    isLoading,
  } = useSelector(selectForm);

  const handleDrag = (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (files.length >= MAX_FILES) {
      setDragDisabled(true);

      return;
    }

    const items = e.dataTransfer.items;

    if (!items?.length || !validateMimeType(items[0].type)) {
      setDragDisabled(true);

      return;
    }

    setDragActive(true);
  };

  const handleLeave = (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDragActive(false);
    setDragDisabled(false);
  };

  const uploadAndSet = async (newFiles: FileList) => {
    const filesToAdd: File[] = [];

    for (let i = 0; i < newFiles.length && files.length + filesToAdd.length < MAX_FILES; i++) {
      const file = newFiles[i];

      if (!validateMimeType(file.type)) {
        continue;
      }

      if (!validateSize(file)) {
        dispatch(setErrors(FILE_SIZE_ERROR));
        continue;
      }

      filesToAdd.push(file);
    }

    if (filesToAdd.length === 0) {
      return;
    }

    dispatch(setErrors({}));

    for (const file of filesToAdd) {
      if (!(await dispatch(uploadTemporaryFile(file)))) {
        continue;
      }
    }

    setFiles(prev => [...prev, ...filesToAdd]);
  };

  const handleDrop = async (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDragActive(false);
    setDragDisabled(false);

    const droppedFiles = e.dataTransfer.files;

    if (!droppedFiles?.length) {
      return;
    }

    await uploadAndSet(droppedFiles);
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.currentTarget.files;

    if (!selectedFiles?.length) {
      return;
    }

    await uploadAndSet(selectedFiles);
  };

  const clearFile = (e: React.MouseEvent, indexToRemove: number) => {
    e.preventDefault();
    setFiles(prev => prev.filter((_, index) => index !== indexToRemove));

    if (files.length === 1) {
      dispatch(resetFileState());
    }
  };

  return (
    <>
      {files.map((file, index) => (
        <Uploader
          key={`${file.name}-${index}`}
          file={file}
          hasError={!!error}
          isFileUploading={isFileUploading}
          dragActive={false}
          dragDisabled={false}
          isDisabled={isLoading}
          handleDrag={() => {}}
          handleLeave={() => {}}
          handleDrop={() => {}}
          handleChange={() => {}}
          handleClickCross={event => clearFile(event, index)}
          isMultiple={true}
        />
      ))}

      {files.length < MAX_FILES && (
        <Uploader
          file={null}
          hasError={!!error}
          isFileUploading={isFileUploading}
          dragActive={dragActive}
          dragDisabled={dragDisabled || files.length >= MAX_FILES}
          isDisabled={isLoading}
          handleDrag={handleDrag}
          handleLeave={handleLeave}
          handleDrop={handleDrop}
          handleChange={handleChange}
          handleClickCross={() => {}}
          isMultiple={true}
        />
      )}
    </>
  );
};

MultiUploaderContainer.displayName = 'MultiUploaderContainer';
